/*
* DashBoardContainer.js
* This component is used to display the dashboard of the application.
* It contains two main components: SoundLevelGauge and SoundLevelDynamicChart.
* There are two SoundLevelGauge components, one for Realtek and one for TI.
* The SoundLevelDynamicChart component displays a dynamic chart of sound levels.
* The tow SoundLevelGauage at the top of the page, and the SoundLevelDynamicChart at the bottom.
* fetch the data from the socket.io server and update the chart in real-time.
*/
import React, { useEffect, useRef, useState } from 'react';
import SoundLevelDynamicChart from './SoundLevelDynamicChart';
import SoundLevelGauge from './SoundLevelGauge';
import VibrationGauge from './VibrationGauge';
import { DMIC_TYPE, dmicTotalGainDb } from '../utils/dmicGain';
import { Box, Typography, Grid, Paper, Divider, CircularProgress } from '@mui/material';
import { io } from 'socket.io-client';
import { useAuthContext } from '../hooks/useAuthContext';

// 歷史資料與即時資料保留的時間範圍（後端 ?hours=24 回傳每分鐘平均）
const SOUND_WINDOW_MS = 24 * 60 * 60 * 1000;

const DashBoardContainer = ({ clientId, title }) => {
  // State for sound levels
  const [realtekLevel, setRealtekLevel] = useState(0);
  const [tiLevel, setTiLevel] = useState(0);
  // Realtek on a DMIC build is a vibration sensor: no SPL, but an unweighted g RMS value ("vpu")
  const [rtkIsDmic, setRtkIsDmic] = useState(false);
  const [dmicTotalGain, setDmicTotalGain] = useState(0);
  const [vpuLevel, setVpuLevel] = useState(0);
  const [soundData, setSoundData] = useState([]);
  const [nn_notify, setNNNotify] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get auth context for token
  const { user } = useAuthContext();
  
  // Reference to socket connection
  const socketRef = useRef(null);
  
  // Fetch historical sound data
  useEffect(() => {
    const fetchSoundData = async () => {
      if (!clientId || !user?.token) return;
      
      try {
        setLoading(true);
        const response = await fetch(`/api/v1/device/spl-avg/${clientId}?hours=24`, {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Fetched SPL data:', data);
        
        // 檢查 data 是否為數組，如果不是，則嘗試處理 data 本身或其屬性
        let dataToProcess = [];
        
        if (Array.isArray(data)) {
          dataToProcess = data;
        } else if (data && typeof data === 'object') {
          // 如果 data 是一個對象，嘗試查找其中的數組屬性
          const possibleArrays = Object.values(data).filter(val => Array.isArray(val));
          if (possibleArrays.length > 0) {
            dataToProcess = possibleArrays[0]; // 使用找到的第一個數組
          } else {
            // 如果沒有數組屬性，將 data 包裝成數組
            dataToProcess = [data];
          }
        }
        
        // Format API response data for chart
        const formattedData = dataToProcess.map(item => {
          let spl = 0;
          
          if (item.splData) {
            spl = parseFloat(item.splData);
          }
          // 使用從API獲取的datetime
          const timestamp = new Date(item.datetime.replace(/(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})/, '$1-$2-$3T$4:$5:$6')); 

          return [
            timestamp,
            spl                      // SPL value
          ];
        }).filter(item => !isNaN(item[1]) && !isNaN(item[0].getTime())); // Filter invalid data
        
        // Sort by time, oldest to newest
        formattedData.sort((a, b) => a[0] - b[0]);
        
        setSoundData(formattedData);
        setError(null);
      } catch (err) {
        console.error('Error fetching sound data:', err);
        setError('Failed to load sound history data');
        setSoundData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSoundData();
    
    // Optional: Set periodic updates
    // const intervalId = setInterval(fetchSoundData, 5 * 60 * 1000); // Update every 5 minutes
    // return () => clearInterval(intervalId);
  }, [clientId, user?.token]);

  // Fetch the Realtek mic type to decide between the SPL gauge and the vibration gauge
  useEffect(() => {
    let isCancelled = false;
    setRtkIsDmic(false);
    setVpuLevel(0);

    const fetchMicType = async () => {
      if (!clientId || !user?.token) return;
      try {
        const response = await fetch(`/api/v1/device/mic-info/${clientId}`, {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!isCancelled) {
          setRtkIsDmic(data?.realtek_type === DMIC_TYPE);
          setDmicTotalGain(dmicTotalGainDb(data?.dmic_gain, data?.adc_vol));
        }
      } catch (err) {
        console.error('Error fetching mic type:', err);
      }
    };

    fetchMicType();
    return () => {
      isCancelled = true;
    };
  }, [clientId, user?.token]);

  // Auto-clear nn_notify after 1 second whenever it changes to a non-empty value
  useEffect(() => {
    if (!nn_notify) return;
    const timer = setTimeout(() => {
      setNNNotify('');
    }, 1500);
    return () => clearTimeout(timer);
  }, [nn_notify]);
  
  // Connect to socket and listen for sound level updates
  useEffect(() => {
    // Create Socket.io connection
    // const port = process.env.REACT_APP_WSPORT;
    // const backendUrl = `${window.location.protocol}//${window.location.hostname}:${port}`;
    // const socket = io(backendUrl, {
    //   transports: ['websocket'],
    //   path: '/socket.io'
    // });
    const socketUrtl = process.env.REACT_APP_SOCKET_URL || window.location.origin;
    const socket = io(socketUrtl, { auth: { token: user?.token } });
    socketRef.current = socket;

    // Listen to socket data for gauge updates
    socket.on(`${clientId}-spl`, (data) => {
      const { deviceName, spl } = data;
      if (deviceName === "vpu") {
        // g RMS, often well below 0.01, so keep full precision
        setVpuLevel(spl);
        return;
      }
      const formattedSpl = parseFloat(spl.toFixed(2));
      console.log("Received SPL data:", formattedSpl);
      if (deviceName === "ti") {
        setTiLevel(formattedSpl);
      } else if (deviceName === "realtek") {
        setRealtekLevel(formattedSpl);
      }
    });

    // Listen for chart data updates
    socket.on(`${clientId}-spl-avg`, (data) => {
      // 解析新的數據點
      try {
        console.log("Received new SPL avg data:", data);
        
        // 確保 splData 和 datetime 都存在
        if (!data.splData || !data.datetime) {
          console.warn("Received incomplete SPL data", data);
          return;
        }
        
        // 解析 SPL 值
        const spl = parseFloat(data.splData);
        
        // 解析時間戳
        const timestamp = new Date(data.datetime.replace(
          /(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})/,
          '$1-$2-$3T$4:$5:$6'
        ));
        
        if (isNaN(spl) || isNaN(timestamp.getTime())) {
          console.warn("Invalid SPL data or timestamp", data);
          return;
        }
        
        // 將新數據點添加到現有數據中
        setSoundData(prevData => {
          // 只保留最近 24 小時，避免長時間開著畫面時資料點無限增加
          const cutoff = timestamp.getTime() - SOUND_WINDOW_MS;
          let start = 0;
          while (start < prevData.length && prevData[start][0].getTime() < cutoff) start++;
          const newData = prevData.slice(start);
          newData.push([timestamp, spl]);

          // 通常新點最新，不需要排序；只有亂序到達時才重新排序（從舊到新）
          const n = newData.length;
          if (n > 1 && newData[n - 2][0] > timestamp) {
            newData.sort((a, b) => a[0] - b[0]);
          }

          return newData;
        });
      } catch (err) {
        console.error("Error processing new SPL data:", err);
      }
    });

    socket.on(`${clientId}-nn-notify`, (data) => {
      console.log("Received nn notify data:", data);
      setNNNotify(data);
    });

    // Notify mqtt_watcher to start sending data
    socket.emit("upload-spl", {
      clientId: clientId,
    });

    // Cleanup function to disconnect socket when component unmounts
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        console.log("Socket disconnected");
      }
    };
  }, [clientId]);
  
  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Dashboard Title */}
      {/* <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
        {title || `Device ${clientId} Dashboard`}
      </Typography> */}
      
      <Grid container spacing={3} sx={{ mb: 4}}>
        <Grid item xs={12} md={6} width={"48%"} height={320}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 2,
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box sx={{ width: '100%'}}>
              {rtkIsDmic ? (
                <VibrationGauge
                  gValue={vpuLevel}
                  totalGainDb={dmicTotalGain}
                  deviceName="Realtek (V2S200D)"
                />
              ) : (
                <SoundLevelGauge 
                  splValue={realtekLevel} 
                  deviceName="Realtek"
                />
              )}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} width={"48%"} height={320}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 2,
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box sx={{ width: '100%'}}>
              <SoundLevelGauge 
                splValue={tiLevel} 
                deviceName="TI"
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Sound Level Chart */}
      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom align="center">
          Sound Level History
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" sx={{ p: 3 }}>
            {error}
          </Typography>
        ) : (
          <SoundLevelDynamicChart 
            title={`${title || 'Device'} Sound Level Monitoring`}
            soundData={soundData}
            minValue={0}
            maxValue={120}
          />
        )}
      </Paper>
      <Box>
        <Typography variant="h6" gutterBottom align="center" color="error">
          {nn_notify} 
        </Typography>
      </Box> 
      <Box sx={{ mt: 4 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Last updated: {new Date().toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default DashBoardContainer;